/* eslint-disable @typescript-eslint/no-explicit-any */
import apiClient, { IresponseData } from "@/lib/api";

// ─── Shared filters ─────────────────────────────────────────────────────────

export interface MetricFilters {
  from?: string;   // YYYY-MM-DD
  to?: string;     // YYYY-MM-DD
  period?: "daily" | "monthly";
}

function removeEmptyFields<T extends Record<string, any>>(obj: T): Partial<T> {
  return Object.entries(obj).reduce((acc, [key, value]) => {
    if (value !== "" && value !== null && value !== undefined) {
      acc[key] = value;
    }
    return acc;
  }, {} as any);
}

// ─── Response types ─────────────────────────────────────────────────────────

export interface ClientGrowthMetric {
  id: string;
  snapshotDate: string;
  totalClients: number;
  activeClients: number;
  newClients: number;
  lostClients: number;
  netGrowth: number;
  retentionRate: string;
  churnRate: string;
  lifetimeValueAvg: number | null;
  createdAt: string;
}

export interface RevenueMetric {
  id: string;
  snapshotDate: string;
  totalRevenue: string;
  totalSubscriptions: number;
  newSubscriptions: number;
  renewedSubscriptions: number;
  cancelledSubscriptions: number;
  avgTicket: string;
  revenueGrowthRate: string | null;
  createdAt: string;
}

export interface SubscriptionStatusMetric {
  id: string;
  snapshotDate: string;
  activeCount: number;
  expiring_7DaysCount: number;
  expiring_3DaysCount: number;
  expiredCount: number;
  cancelledCount: number;
  totalCount: number;
  healthRate: string;
  createdAt: string;
}

export interface PlanDistributionMetric {
  id: string;
  snapshotDate: string;
  durationDays: number;
  subscriptionCount: number;
  revenue: string;
  percentage: string;
  avgPrice: string;
  createdAt: string;
}

export interface ServiceRevenueMetric {
  id: string;
  snapshotDate: string;
  serviceId: string;
  serviceName: string;
  revenue: string;
  subscriptionCount: number;
  slotCount: number;
  fullAccountCount: number;
  growthVsPrevDay: string | null;
  ranking: number;
  createdAt: string;
}

export interface SlotOccupancyMetric {
  id: string;
  snapshotDate: string;
  [key: string]: any;
}

export interface DashboardMetric {
  activeClients: number;
  activeSubscriptions: number;
  expiringIn3Days: number;
  monthlyRevenue: number;
}

// ─── Service ─────────────────────────────────────────────────────────────────

export const metricsService = {
  async getClientGrowth(filters?: MetricFilters): Promise<IresponseData<ClientGrowthMetric[]>> {
    try {
      const params = filters ? removeEmptyFields(filters) : {};
      const response = await apiClient.get<IresponseData<ClientGrowthMetric[]>>(
        "/metrics/client-growth",
        { params }
      );
      return response.data;
    } catch (error) {
      throw new Error(
        error.response?.data?.message || "Error al obtener métricas de crecimiento de clientes."
      );
    }
  },

  async getRevenue(filters?: MetricFilters): Promise<IresponseData<RevenueMetric[]>> {
    try {
      const params = filters ? removeEmptyFields(filters) : {};
      const response = await apiClient.get<IresponseData<RevenueMetric[]>>(
        "/metrics/revenue",
        { params }
      );
      return response.data;
    } catch (error) {
      throw new Error(
        error.response?.data?.message || "Error al obtener métricas de ingresos."
      );
    }
  },

  async getSubscriptionStatus(filters?: MetricFilters): Promise<IresponseData<SubscriptionStatusMetric[]>> {
    try {
      const params = filters ? removeEmptyFields(filters) : {};
      const response = await apiClient.get<IresponseData<SubscriptionStatusMetric[]>>(
        "/metrics/subscription-status",
        { params }
      );
      return response.data;
    } catch (error) {
      throw new Error(
        error.response?.data?.message || "Error al obtener métricas de estado de suscripciones."
      );
    }
  },

  async getPlanDistribution(filters?: MetricFilters): Promise<IresponseData<PlanDistributionMetric[]>> {
    try {
      const params = filters ? removeEmptyFields(filters) : {};
      const response = await apiClient.get<IresponseData<PlanDistributionMetric[]>>(
        "/metrics/plan-distribution",
        { params }
      );
      return response.data;
    } catch (error) {
      throw new Error(
        error.response?.data?.message || "Error al obtener distribución de planes."
      );
    }
  },

  async getServiceRevenue(filters?: MetricFilters): Promise<IresponseData<ServiceRevenueMetric[]>> {
    try {
      const params = filters ? removeEmptyFields(filters) : {};
      const response = await apiClient.get<IresponseData<ServiceRevenueMetric[]>>(
        "/metrics/service-revenue",
        { params }
      );
      return response.data;
    } catch (error) {
      throw new Error(
        error.response?.data?.message || "Error al obtener ingresos por servicio."
      );
    }
  },

  async getSlotOccupancy(filters?: MetricFilters): Promise<IresponseData<SlotOccupancyMetric[]>> {
    try {
      const params = filters ? removeEmptyFields(filters) : {};
      const response = await apiClient.get<IresponseData<SlotOccupancyMetric[]>>(
        "/metrics/slot-occupancy",
        { params }
      );
      return response.data;
    } catch (error) {
      throw new Error(
        error.response?.data?.message || "Error al obtener ocupación de slots."
      );
    }
  },

  async getDashboard(): Promise<IresponseData<DashboardMetric>> {
    try {
      const response = await apiClient.get<IresponseData<DashboardMetric>>("/metrics/dashboard");
      return response.data;
    } catch (error) {
      throw new Error(
        error.response?.data?.message || "Error al obtener métricas del dashboard."
      );
    }
  },
};
